import { Box, BoxProps, Button, Typography } from '@mui/material';
import { useState } from 'react';

import { type InputState, useInputState } from '../../utils/inputState';
import { type Children, type ReturnComponent } from '../../utils/reactTypes';
import { AxleTextField, AxleTextFieldProps } from '../formFields/AxleTextField';
import { NoneLabel } from '../labels/NoneLabel';
import {
    LeftRailNoneLabel,
    LeftRailTextButton,
    LeftRailValueTypography,
} from '../patients/PatientLeftRail/PatientLeftRail';
import { HorizontalStack, VerticalStack } from '../utils/linearLayouts';
import { NotesInfoIcon } from './NotesInfoIcon';

export function NotesDisplay({
    notes,
    whiteBackground = false,
}: {
    notes: string | null;
    whiteBackground?: boolean;
}) {
    return (
        <Box
            sx={{
                p: 1,
                backgroundColor: whiteBackground ? '#fff' : '#fafafa',
                border: '1px solid #f3f3f3',
                borderRadius: 0.5,
                overflowWrap: 'break-word',
            }}
        >
            {notes ? (
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{notes}</Typography>
            ) : (
                <NoneLabel />
            )}
        </Box>
    );
}

type NotesInputProps = BoxProps & {
    notes: InputState<string>;
    readOnly?: boolean;
    _extraInputProps?: AxleTextFieldProps['_extraInputProps'];
};
export const NotesInput = ({
    notes,
    readOnly = false,
    _extraInputProps,
    ...rest
}: NotesInputProps) => {
    return (
        <Box
            sx={{
                backgroundColor: '#fafafa',
                borderRadius: '8px',
                overflowWrap: 'break-word',
            }}
            {...rest}
        >
            {readOnly ? (
                notes.value ? (
                    <LeftRailValueTypography sx={{ whiteSpace: 'pre-wrap' }}>
                        {notes.value}
                    </LeftRailValueTypography>
                ) : (
                    <LeftRailNoneLabel />
                )
            ) : (
                <AxleTextField
                    inputState={notes}
                    multiline={true}
                    _extraInputProps={_extraInputProps}
                />
            )}
        </Box>
    );
};

const EditCancelButtons = ({
    isEditing,
    onCancel,
    onSubmit,
    onEdit,
    disableSave,
}: {
    isEditing: boolean;
    onCancel: () => void;
    onSubmit: () => void;
    onEdit: () => void;
    disableSave: boolean;
}): ReturnComponent =>
    isEditing ? (
        <HorizontalStack gap='2px' alignItems='center' fullWidth={false}>
            <LeftRailTextButton onClick={onCancel}>Cancel</LeftRailTextButton>
            <Button
                size='small'
                variant='contained'
                disabled={disableSave}
                sx={{
                    textTransform: 'none',
                    minWidth: '45px',
                    padding: '2px 5px',
                }}
                onClick={onSubmit}
            >
                Save
            </Button>
        </HorizontalStack>
    ) : (
        <LeftRailTextButton onClick={onEdit}>Edit</LeftRailTextButton>
    );

export interface NotesEditorProps {
    notes: string | null;
    canView: boolean;
    canEdit: boolean;
    onSave: (notes: string) => Promise<void>;
    notesInfoIconProps: {
        visibleToClinicians: boolean;
        headline: string;
        description: string;
    };
    captionLabel: Children;
    transformNotesBeforeSave?: (notes: string) => string | null;
}

export const NotesEditor = ({
    notes,
    canView,
    canEdit,
    onSave,
    notesInfoIconProps,
    captionLabel,
    transformNotesBeforeSave,
}: NotesEditorProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const notesState = useInputState<string>(notes ?? '');
    const [isSaving, setIsSaving] = useState(false);

    const onCancel = () => {
        setIsEditing(false);
        notesState.reset();
    };

    const onSubmit = async () => {
        if (!notesState.isValid) return;
        if (!notesState.isDifferent) return;

        setIsSaving(true);
        try {
            const notesToSave = transformNotesBeforeSave
                ? transformNotesBeforeSave(notesState.value)
                : notesState.value;
            await onSave(notesToSave ?? '');
            setIsEditing(false);
        } finally {
            setIsSaving(false);
        }
    };

    if (!canView) {
        return null;
    }

    return (
        // No spacing because the caption header ends up being taller than in other left rail sections.
        <VerticalStack spacing={0}>
            <HorizontalStack>
                <NotesInfoIcon {...notesInfoIconProps}>{captionLabel}</NotesInfoIcon>
                {canEdit ? (
                    <EditCancelButtons
                        isEditing={isEditing}
                        onCancel={onCancel}
                        onSubmit={() => {
                            void onSubmit();
                        }}
                        onEdit={() => setIsEditing(true)}
                        disableSave={!notesState.isValid || !notesState.isDifferent || isSaving}
                    />
                ) : undefined}
            </HorizontalStack>
            <NotesInput
                notes={notesState}
                readOnly={isEditing ? false : true}
                sx={{ backgroundColor: 'transparent' }}
                _extraInputProps={{
                    sx: {
                        fontSize: '14px',
                        lineHeight: '20px',
                        backgroundColor: 'transparent',
                    },
                }}
            />
        </VerticalStack>
    );
};
